/**
 * Dish detail route (Organizers only)
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useDish, useDeleteDish } from '@/hooks/useDishes';
import { useDishPreferences, useSetDishPreference } from '@/hooks/usePreferences';
import { useUsers } from '@/hooks/useUsers';
import { requireOrganizer } from '@/lib/auth/routeGuards';
import { Loading } from '@/components/ui/Loading';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { NutritionSummary } from '@/components/dishes/NutritionSummary';
import { useToast } from '@/components/ui/ToastProvider';

export const Route = createFileRoute('/dishes/$dishId')({
  beforeLoad: requireOrganizer,
  component: DishDetailComponent,
});

function DishDetailComponent() {
  const { dishId } = Route.useParams();
  const id = parseInt(dishId, 10);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { data: dish, isLoading, error } = useDish(id);
  const deleteDish = useDeleteDish();
  const { data: membersData } = useUsers();
  const { data: preferences } = useDishPreferences(id);
  const setDishPreference = useSetDishPreference();

  const prefByUser = new Map(
    (preferences ?? []).map((p) => [p.userId, p.value]),
  );

  const handlePreferenceChange = async (userId: number, value: string) => {
    try {
      await setDishPreference.mutateAsync({
        dishId: id,
        data: {
          userId,
          value: (value || 'neutral') as 'like' | 'neutral' | 'dislike',
        },
      });
    } catch {
      toast.error(t('preferences.errorSaving'));
    }
  };

  const handleDelete = async () => {
    if (!dish) return;
    if (!window.confirm(t('dishes.deleteConfirm', { name: dish.name }))) return;
    try {
      await deleteDish.mutateAsync(dish.id);
      toast.success(t('dishes.deleted'));
      navigate({ to: '/dishes' });
    } catch {
      toast.error(t('dishes.errorDeleting'));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Loading />
        </div>
      </div>
    );
  }

  if (error || !dish) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            onClick={() => navigate({ to: '/dishes' })}
            className="mb-4 text-sm font-medium text-indigo-600 hover:text-indigo-800"
          >
            ← {t('dishes.backToList')}
          </button>
          <ErrorMessage
            title={t('dishes.notFound')}
            message={t('dishes.notFoundDescription')}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate({ to: '/dishes' })}
          className="mb-6 text-sm font-medium text-indigo-600 hover:text-indigo-800"
        >
          ← {t('dishes.backToList')}
        </button>

        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{dish.name}</h1>
            <p className="mt-1 text-sm text-gray-600">
              {t('dishes.servingsCount', { count: dish.servings })}
              {dish.goalOverride
                ? ` · ${t(`goal.${dish.goalOverride}`)}`
                : ''}
            </p>
          </div>
          <button
            onClick={() => navigate({ to: `/dishes/${dish.id}/edit` })}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
          >
            {t('common.edit')}
          </button>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">
            {t('dishes.nutrition')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <NutritionSummary
              nutrition={dish.nutrition.total}
              title={t('dishes.total')}
            />
            <NutritionSummary
              nutrition={dish.nutrition.perServing}
              title={t('dishes.perServing')}
            />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">
            {t('dishes.ingredients')}
          </h2>
          <ul className="divide-y divide-gray-100">
            {dish.ingredients.map((ingredient) => (
              <li
                key={ingredient.id}
                className="flex items-center justify-between py-3"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {ingredient.productName}
                    {ingredient.removable && (
                      <span className="ml-2 text-xs text-gray-400">
                        ({t('dishes.removable')})
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500">
                    {ingredient.quantityG} g
                  </p>
                </div>
                <NutritionSummary nutrition={ingredient.nutrition} />
              </li>
            ))}
          </ul>
        </div>

        {/* Member preferences */}
        <div className="mt-6 bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">
            {t('preferences.dishTitle')}
          </h2>
          {(membersData?.users ?? []).length === 0 ? (
            <p className="text-sm text-gray-500">{t('preferences.noMembers')}</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {membersData!.users.map((member) => (
                <li
                  key={member.id}
                  className="flex items-center justify-between py-2"
                >
                  <span className="text-sm text-gray-900">{member.name}</span>
                  <select
                    value={prefByUser.get(member.id) ?? 'neutral'}
                    onChange={(e) =>
                      handlePreferenceChange(member.id, e.target.value)
                    }
                    className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                  >
                    <option value="like">{t('preference.dish.like')}</option>
                    <option value="neutral">
                      {t('preference.dish.neutral')}
                    </option>
                    <option value="dislike">
                      {t('preference.dish.dislike')}
                    </option>
                  </select>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-6">
          <button
            onClick={handleDelete}
            className="text-sm font-medium text-red-600 hover:text-red-800"
          >
            {t('dishes.delete')}
          </button>
        </div>
      </div>
    </div>
  );
}
